import { createColumnHelper } from '@tanstack/react-table';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { TFunction } from 'i18next';
import { Image, Tooltip } from '@chakra-ui/react';
import placeholderImg from '@texas/assets/placeholder-50.png';
import { Icons } from '../shared/Icons';
import { StateIndicator } from '../shared/indicators/StateIndicator';
import { StepsIndicator } from '../shared/indicators/StepsIndicator';
import { clientEndpoints } from '@texas/clientEndpoints';
import { AngularNavLink } from '../shared/AngularNavLink';
import { ExpandImage } from '../shared/image/ExpandImage';
import { SearchVariantItem } from '@texas/api/endpoints/search/searchVariantsTypes';
import { ArticleProcess } from '@texas/api/endpoints/variantsApi';
import { ArticleState } from '@texas/api/endpoints/articlesApi';
import { FilteredTagsCell } from '../shared/tags/FilteredTagsCell';
import { TagColumnHeader } from '../shared/tags/TagColumnHeader';

export const TableColumns = (t: TFunction, slim?: boolean) => {
  const columnHelper = createColumnHelper<SearchVariantItem>();

  const columns = [];
  columns.push(
    columnHelper.accessor('imageIdentifier', {
      cell: (info) => (
        <ExpandImage identifier={info.getValue()} variant="scale">
          <Image
            boxSize="12"
            src={
              info.getValue()
                ? clientEndpoints.previewImage(info.getValue(), 256)
                : undefined
            }
            fallbackSrc={placeholderImg}
            objectFit="contain"
          />
        </ExpandImage>
      ),
      header: t('general.photo'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  columns.push(
    columnHelper.accessor('syncedToErp', {
      cell: (info) =>
        info.getValue() ? (
          <Tooltip label={t('garp.syncedToGarp')}>
            <span>
              <Icons.BriefcaseArrowLeftRight boxSize={4} />
            </span>
          </Tooltip>
        ) : null,
      header: t('garp.syncedToGarp'),
      enableSorting: false,
      size: 60,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  if (!slim) {
    columns.push(
      columnHelper.accessor('mainVariant', {
        cell: (info) => (info.getValue() ? <Icons.Checkmark /> : ''),
        header: t('general.main'),
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }

  columns.push(
    columnHelper.accessor('customerName', {
      cell: (info) => (
        <AngularNavLink
          to="customer"
          navobj={{ customerId: info.row.original.customerId }}
        >
          {info.getValue()} - {info.row.original.customerIdentifier}
        </AngularNavLink>
      ),
      header: t('general.brand'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );
  columns.push(
    columnHelper.accessor('concept', {
      cell: (info) => (
        <AngularNavLink
          to="concept"
          navobj={{ conceptId: info.row.original.conceptId }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.concept'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );
  columns.push(
    columnHelper.accessor('itemCode', {
      cell: (info) => (
        <AngularNavLink
          to="article"
          navobj={{ articleId: info.row.original.articleId }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.itemCode'),
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  columns.push(
    columnHelper.accessor('variantName', {
      cell: (info) => (
        <AngularNavLink
          to="variant"
          navobj={{
            variantId: info.row.original.variantId,
            articleId: info.row.original.articleId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('variant.variant'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );
  columns.push(
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: t('general.description'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  if (!slim) {
    columns.push(
      columnHelper.accessor('color', {
        cell: (info) => info.getValue(),
        header: t('general.color'),
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }

  if (!slim) {
    columns.push(
      columnHelper.accessor('size', {
        cell: (info) => info.getValue(),
        header: t('general.size'),
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }

  columns.push(
    columnHelper.accessor('productGroup', {
      cell: (info) => info.getValue(),
      header: t('general.productGroup'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  columns.push(
    columnHelper.accessor('tags', {
      cell: (info) => {
        const tags = info.getValue();
        if (tags.length === 0) return null;
        return <FilteredTagsCell tags={tags} />;
      },
      header: () => <TagColumnHeader />,
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  if (!slim) {
    columns.push(
      columnHelper.accessor('material', {
        cell: (info) => info.getValue(),
        header: t('general.material'),
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }
  columns.push(
    columnHelper.accessor('created', {
      cell: (info) => formatDate(Locale.En, info.getValue()),
      header: t('article.created'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
  );

  if (!slim) {
    columns.push(
      columnHelper.accessor('branch', {
        cell: (info) => info.getValue(),
        header: t('general.branch'),
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }

  if (!slim) {
    columns.push(
      columnHelper.accessor('state', {
        cell: (info) => (
          <StateIndicator
            state={
              ArticleState[
                info.getValue() as unknown as keyof typeof ArticleState
              ]
            }
          />
        ),
        header: t('general.state'),
        enableSorting: false,
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }

  if (!slim) {
    columns.push(
      columnHelper.accessor('articleProcess', {
        cell: (info) =>
          ArticleState[
            info.row.original.state as unknown as keyof typeof ArticleState
          ] == ArticleState.Cancelled ? null : (
            <StepsIndicator
              currentStep={
                ArticleProcess[
                  info.getValue() as unknown as keyof typeof ArticleProcess
                ]
              }
            />
          ),
        enableSorting: false,
        header: t('general.articleProcess'),
        meta: { toggleable: true, defaultVisible: true },
      }),
    );
  }

  return columns;
};
